import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { parseSearchParams } from '@/utils/stringUtils';
import { detokenize } from '@/utils/tokenizer';
import { logError } from '@/utils/logError';

interface UsePostHogDistinctIdResult {
  distinctId: string | undefined;
  loading: boolean;
}

const sentryInfo = (message: string, data: Record<string, unknown>) => {
  Sentry.captureMessage(`${message} | ${JSON.stringify(data)}`, { level: 'info', tags: { posthog_debug: 'true' } });
};

/**
 * Hook to get PostHog distinct ID from URL params.
 * Supports both legacy ph_distinct_id and new ph_token.
 * ph_distinct_id takes precedence (backward compatibility).
 */
export const usePostHogDistinctId = (): UsePostHogDistinctIdResult => {
  const location = useLocation();
  const searchParams = parseSearchParams(location.search);

  const urlDistinctId = searchParams.ph_distinct_id?.trim();
  const urlToken = searchParams.ph_token?.trim();

  const [distinctId, setDistinctId] = useState<string | undefined>(urlDistinctId);
  const [loading, setLoading] = useState<boolean>(!urlDistinctId && !!urlToken);

  useEffect(() => {
    // ph_distinct_id takes precedence (backward compatibility)
    if (urlDistinctId) {
      sentryInfo('[PostHog Debug][usePostHogDistinctId] ph_distinct_id from URL', { urlDistinctId });
      setDistinctId(urlDistinctId);
      setLoading(false);
      return;
    }

    // If no token, nothing to do
    if (!urlToken) {
      sentryInfo('[PostHog Debug][usePostHogDistinctId] No ph_distinct_id or ph_token in URL', {
        search: location.search,
      });
      return;
    }

    // Detokenize the ph_token
    const fetchDistinctId = async () => {
      sentryInfo('[PostHog Debug][usePostHogDistinctId] Detokenizing ph_token', { urlToken });
      setLoading(true);

      const result = await detokenize(urlToken);

      if (result.success && result.value) {
        sentryInfo('[PostHog Debug][usePostHogDistinctId] ph_token detokenized', { distinctId: result.value });
        setDistinctId(result.value);
      } else {
        logError('[usePostHogDistinctId]', 'detokenization failed', result.error || 'Unknown error');
        sentryInfo('[PostHog Debug][usePostHogDistinctId] ph_token detokenization failed', {
          error: String(result.error),
        });
      }

      setLoading(false);
    };

    fetchDistinctId();
  }, [urlDistinctId, urlToken]);

  return { distinctId, loading };
};
